import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Title, Meta, Link as HeadLink } from "react-head";
import { motion } from "motion/react";
import { useParams, useNavigate } from "react-router-dom";
import { FiEye, FiArrowLeft, FiGrid, FiTrendingUp } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";

import ClientCalling from "../api/ClientCalling";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import SmartImage from "../components/Client/SmartImage";
import ClientAdSense from "../components/Client/ClientAdSense";
import { formatView } from "../components/formatView";

const CardSkeleton = () => (
    <div className='relative aspect-[5/6] rounded-2xl ios-shimmer overflow-hidden'>
        <div
            className='absolute top-2 left-2 w-10 h-4 rounded-full'
            style={{ background: "rgba(255,255,255,0.08)" }}
        />
        <div className='absolute inset-x-2 bottom-2 hidden md:block'>
            <div
                className='h-9 rounded-lg'
                style={{
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(8px)",
                }}
            />
        </div>
    </div>
);

const HeroSkeleton = () => (
    <div className='relative w-full overflow-hidden'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 md:px-8 pt-6 md:pt-10 pb-6 md:pb-8'>
            <div className='ios-glass-dark rounded-3xl p-5 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-7'>
                <div className='ios-shimmer w-24 h-24 md:w-28 md:h-28 rounded-2xl flex-shrink-0' />
                <div className='flex-1 w-full min-w-0 space-y-3'>
                    <div className='h-3 w-24 rounded-full ios-shimmer' />
                    <div className='h-8 md:h-10 w-2/3 rounded-md ios-shimmer' />
                    <div className='h-4 w-1/2 rounded-md ios-shimmer opacity-60' />
                    <div className='flex gap-2 mt-2'>
                        <div className='h-7 w-20 rounded-full ios-shimmer' />
                        <div className='h-7 w-24 rounded-full ios-shimmer' />
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const ItemCard = ({ item, index, onClick }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
            duration: 0.5,
            delay: Math.min(index * 0.03, 0.3),
            ease: [0.22, 1, 0.36, 1],
        }}
        whileHover={{ y: -5 }}
        whileTap={{ scale: 0.97 }}
        onClick={onClick}
        className='relative aspect-[5/6] rounded-2xl cursor-pointer group bg-client-back'
        style={{
            overflow: "clip",
            boxShadow:
                "0 10px 26px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)",
        }}
    >
        <SmartImage
            src={item.l_image}
            alt={item.l_name}
            className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
        />
        <div
            className='absolute inset-x-0 bottom-0 h-3/5 pointer-events-none'
            style={{
                background:
                    "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 40%, transparent 100%)",
            }}
        />
        <div className='absolute top-2 left-2 ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[9px] md:text-[11px] font-medium pointer-events-none'>
            <FiEye className='text-[10px] md:text-xs' />
            <span className='tabular-nums'>{formatView(item.l_view)}</span>
        </div>
        <div className='absolute inset-x-0 bottom-0 p-2 md:p-3 flex items-center justify-between gap-2'>
            <p
                className='text-white text-[10px] md:text-xs font-semibold truncate flex-1'
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}
            >
                {item.l_name}
            </p>
            <div className='ios-glass-btn hidden md:flex px-2.5 py-1 rounded-full text-[9px] md:text-[10px] font-medium text-white items-center gap-1 whitespace-nowrap'>
                Get Prompt
            </div>
        </div>
        <div
            className='absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
            style={{
                boxShadow:
                    "inset 0 0 0 2px rgba(127,0,255,0.5), 0 0 28px rgba(127,0,255,0.35)",
            }}
        />
    </motion.div>
);

const ClientCategory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const observerTarget = useRef(null);

    const [category, setCategory] = useState(null);
    const [listData, setListData] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [nextPage, setNextPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalList, setTotalList] = useState(0);
    const [totalView, setTotalView] = useState(0);

    useEffect(() => {
        setListData([]);
        setPage(1);
        setNextPage(null);
        setLoading(true);
    }, [id]);

    const fetchCategory = useCallback(
        async (pageNum) => {
            try {
                if (pageNum === 1) setLoading(true);
                else setLoadingMore(true);

                const resp = await ClientCalling?.getCategoryClientByID(
                    pageNum,
                    limit,
                    id
                );
                if (resp?.status) {
                    setCategory(resp.data);
                    setListData((prev) =>
                        pageNum === 1
                            ? Array.isArray(resp.list)
                                ? resp.list
                                : []
                            : [...prev, ...(Array.isArray(resp.list) ? resp.list : [])]
                    );
                    setNextPage(resp.nextPage || null);
                    setTotalList(resp.totalList || 0);
                    setTotalView(resp.totalview || resp.totalView || 0);
                } else if (pageNum === 1) {
                    setListData([]);
                }
            } catch (err) {
                console.error("Category fetch failed:", err);
                if (pageNum === 1) setListData([]);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [id, limit]
    );

    useEffect(() => {
        fetchCategory(page);
    }, [page, id, fetchCategory]);

    // Shuffle + pick 4 random items for the hero collage.
    // Re-shuffles whenever the underlying list grows by a full page.
    const randomPicks = useMemo(() => {
        if (!listData.length) return [];
        const arr = [...listData];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr.slice(0, 4);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, listData.length]);

    useEffect(() => {
        const el = observerTarget.current;
        if (!el || !nextPage) return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    nextPage &&
                    !loading &&
                    !loadingMore
                ) {
                    setPage(nextPage);
                }
            },
            { rootMargin: "300px", threshold: 0 }
        );
        obs.observe(el);
        return () => obs.unobserve(el);
    }, [nextPage, loading, loadingMore]);

    const title = category
        ? `${category.category_name} | PicPromptify`
        : "PicPromptify Categories";
    const description = category
        ? `Explore ${category.category_name} AI prompts and avatars on PicPromptify. Browse ${totalList} creative options and get inspired.`
        : "Browse categories of AI prompts and avatars on PicPromptify.";
    const keywords = category
        ? `${category.category_name}, AI prompts, avatars, PicPromptify, AI art, creative prompts`
        : "AI prompts, avatars, PicPromptify, categories";

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <Title>{title}</Title>
            <Meta name='description' content={description} />
            <Meta name='keywords' content={keywords} />
            <Meta property='og:title' content={title} />
            <Meta property='og:description' content={description} />
            <Meta property='og:type' content='website' />
            <Meta
                property='og:url'
                content={`https://picpromptify.com/category/${id}`}
            />
            <Meta
                property='og:image'
                content={category?.category_image || "/default-category.jpg"}
            />
            <HeadLink
                rel='canonical'
                href={`https://picpromptify.com/category/${id}`}
            />

            <ClientNavbar />
            <FixedButton />

            {/* ===== HERO ===== */}
            {loading && page === 1 ? (
                <HeroSkeleton />
            ) : (
                category && (
                    <section className='relative w-full overflow-hidden'>
                        {/* Ambient blurred backdrop using category image */}
                        {category.category_image && (
                            <div
                                aria-hidden
                                className='absolute inset-0 pointer-events-none'
                            >
                                <img
                                    src={category.category_image}
                                    alt=''
                                    className='absolute inset-0 w-full h-full object-cover'
                                    style={{
                                        filter: "blur(80px) saturate(1.4)",
                                        opacity: 0.35,
                                        transform: "scale(1.1)",
                                    }}
                                />
                                <div
                                    className='absolute inset-0'
                                    style={{
                                        background:
                                            "linear-gradient(to bottom, rgba(10,8,16,0.4) 0%, rgba(10,8,16,0.75) 60%, rgba(10,8,16,0.95) 100%)",
                                    }}
                                />
                            </div>
                        )}

                        {/* Subtle gradient orbs on top */}
                        <div
                            aria-hidden
                            className='absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-30 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                            }}
                        />
                        <div
                            aria-hidden
                            className='absolute top-0 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                            }}
                        />

                        <div className='relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8 pt-5 md:pt-8 pb-8 md:pb-14'>
                            {/* Top action bar */}
                            <div className='flex items-center justify-between mb-6 md:mb-10'>
                                <motion.button
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    whileHover={{ x: -4 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => navigate(-1)}
                                    className='ios-glass-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font/85 text-[11px] md:text-xs font-medium'
                                >
                                    <FiArrowLeft className='text-sm' />
                                    Back
                                </motion.button>

                                {/* Breadcrumb */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.1 }}
                                    className='hidden md:flex items-center gap-1.5 text-client-font/45 text-[11px] tracking-wider uppercase font-medium'
                                >
                                    <button
                                        onClick={() => navigate("/")}
                                        className='hover:text-client-font transition-colors'
                                    >
                                        Home
                                    </button>
                                    <span className='text-client-font/30'>/</span>
                                    <span className='text-client-font/75'>
                                        Category
                                    </span>
                                </motion.div>
                            </div>


                            {/* Hero content */}
                            <div className='grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center'>
                                {/* Category image — large, premium */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{
                                        duration: 0.6,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className='md:col-span-4 lg:col-span-3'
                                >
                                    <div
                                        className='relative aspect-square w-40 mx-auto md:w-full rounded-3xl overflow-hidden group'
                                        style={{
                                            background:
                                                "linear-gradient(145deg, rgba(127,0,255,0.18), rgba(18,18,18,0.6))",
                                            border: "1px solid rgba(255,255,255,0.14)",
                                            boxShadow:
                                                "0 30px 70px -20px rgba(127,0,255,0.55), 0 14px 32px -10px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.18)",
                                        }}
                                    >
                                        {category.category_image ? (
                                            <>
                                                <SmartImage
                                                    src={category.category_image}
                                                    alt={category.category_name}
                                                    eager
                                                    className='absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-105'
                                                />
                                                {/* Glossy top highlight */}
                                                <div
                                                    aria-hidden
                                                    className='absolute inset-x-3 top-2 h-1/3 rounded-full pointer-events-none opacity-50'
                                                    style={{
                                                        background:
                                                            "linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)",
                                                        filter: "blur(8px)",
                                                    }}
                                                />
                                                {/* Glass ring */}
                                                <div
                                                    aria-hidden
                                                    className='absolute inset-0 rounded-3xl pointer-events-none'
                                                    style={{
                                                        boxShadow:
                                                            "inset 0 0 0 1px rgba(255,255,255,0.14)",
                                                    }}
                                                />
                                            </>
                                        ) : randomPicks.length > 0 ? (
                                            <>
                                                {/* Floating polaroid stack — random picks */}
                                                <div
                                                    aria-hidden
                                                    className='absolute inset-0 flex items-center justify-center'
                                                    style={{ perspective: "800px" }}
                                                >
                                                    {randomPicks.map((it, i) => {
                                                        const tilts = [-14, -4, 6, 16];
                                                        const offsetsX = [-26, -10, 10, 26];
                                                        const offsetsY = [10, -6, -2, 8];
                                                        const scales = [0.78, 0.88, 0.88, 0.78];
                                                        return (
                                                            <motion.div
                                                                key={it._id}
                                                                initial={{
                                                                    opacity: 0,
                                                                    scale: 0.4,
                                                                    rotate: tilts[i] * 2,
                                                                    y: 30,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    scale: scales[i],
                                                                    rotate: tilts[i],
                                                                    y: offsetsY[i],
                                                                    x: offsetsX[i] + "%",
                                                                }}
                                                                transition={{
                                                                    delay: i * 0.1,
                                                                    type: "spring",
                                                                    stiffness: 180,
                                                                    damping: 18,
                                                                }}
                                                                className='absolute w-2/3 aspect-[3/4] rounded-xl overflow-hidden'
                                                                style={{
                                                                    zIndex:
                                                                        i === 1 || i === 2
                                                                            ? 3
                                                                            : 2,
                                                                    background:
                                                                        "rgba(255,255,255,0.06)",
                                                                    border:
                                                                        "2px solid rgba(255,255,255,0.18)",
                                                                    boxShadow:
                                                                        "0 14px 30px -10px rgba(0,0,0,0.6), 0 6px 14px -6px rgba(127,0,255,0.4)",
                                                                }}
                                                            >
                                                                <img
                                                                    src={it.l_image}
                                                                    alt=''
                                                                    className='absolute inset-0 w-full h-full object-cover'
                                                                    loading='lazy'
                                                                />
                                                                {/* Top highlight on each photo */}
                                                                <div
                                                                    aria-hidden
                                                                    className='absolute inset-x-1 top-1 h-1/4 rounded-md pointer-events-none opacity-50'
                                                                    style={{
                                                                        background:
                                                                            "linear-gradient(to bottom, rgba(255,255,255,0.3), transparent)",
                                                                    }}
                                                                />
                                                            </motion.div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Soft radial dark vignette */}
                                                <div
                                                    aria-hidden
                                                    className='absolute inset-0 pointer-events-none'
                                                    style={{
                                                        background:
                                                            "radial-gradient(ellipse at center, transparent 30%, rgba(10,8,16,0.55) 100%)",
                                                    }}
                                                />
                                                {/* Glass ring */}
                                                <div
                                                    aria-hidden
                                                    className='absolute inset-0 rounded-3xl pointer-events-none'
                                                    style={{
                                                        boxShadow:
                                                            "inset 0 0 0 1px rgba(255,255,255,0.14)",
                                                    }}
                                                />
                                            </>
                                        ) : null}
                                    </div>
                                </motion.div>

                                {/* Info */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: 0.1,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className='md:col-span-8 lg:col-span-9 text-left'
                                >
                                    {/* Eyebrow */}
                                    <div className='inline-flex items-center gap-1.5 ios-glass px-2.5 py-1 rounded-full text-[10px] md:text-xs text-client-font/85 mb-3'>
                                        <HiSparkles
                                            style={{ color: "#FCFA02", fontSize: 11 }}
                                        />
                                        <span className='tracking-[0.18em] uppercase font-semibold'>
                                            Category Collection
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h1 className='text-3xl md:text-5xl lg:text-6xl font-bold text-client-font tracking-tight leading-[1.05]'>
                                        {category.category_name}
                                    </h1>

                                    {/* Description */}
                                    {category.category_description ? (
                                        <p className='text-client-font/60 text-sm md:text-base mt-3 md:mt-4 max-w-2xl leading-relaxed'>
                                            {category.category_description}
                                        </p>
                                    ) : (
                                        <p className='text-client-font/55 text-sm md:text-base mt-3 md:mt-4 max-w-2xl leading-relaxed'>
                                            Curated collection of{" "}
                                            <span className='text-client-title font-semibold'>
                                                {category.category_name}
                                            </span>{" "}
                                            AI prompts — explore styles, copy prompts, and
                                            bring your ideas to life.
                                        </p>
                                    )}

                                    {/* Stats — minimal inline row */}
                                    <div className='mt-5 md:mt-6 flex flex-wrap items-center gap-x-6 gap-y-3'>
                                        <div className='inline-flex items-center gap-2'>
                                            <div
                                                className='w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0'
                                                style={{
                                                    background:
                                                        "linear-gradient(135deg, rgba(127,0,255,0.28), rgba(127,0,255,0.06))",
                                                    border:
                                                        "1px solid rgba(127,0,255,0.35)",
                                                }}
                                            >
                                                <FiGrid
                                                    className='text-xs'
                                                    style={{ color: "#FCFA02" }}
                                                />
                                            </div>
                                            <div className='leading-tight'>
                                                <p className='text-client-font font-bold text-base md:text-lg tabular-nums'>
                                                    {totalList}
                                                </p>
                                                <p className='text-client-font/50 text-[9px] md:text-[10px] uppercase tracking-wider font-semibold'>
                                                    Avatars
                                                </p>
                                            </div>
                                        </div>

                                        {totalView > 0 && (
                                            <div className='inline-flex items-center gap-2'>
                                                <div
                                                    className='w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0'
                                                    style={{
                                                        background:
                                                            "linear-gradient(135deg, rgba(252,250,2,0.22), rgba(252,250,2,0.04))",
                                                        border:
                                                            "1px solid rgba(252,250,2,0.3)",
                                                    }}
                                                >
                                                    <FiEye
                                                        className='text-xs'
                                                        style={{ color: "#FCFA02" }}
                                                    />
                                                </div>
                                                <div className='leading-tight'>
                                                    <p className='text-client-font font-bold text-base md:text-lg tabular-nums'>
                                                        {formatView(totalView)}
                                                    </p>
                                                    <p className='text-client-font/50 text-[9px] md:text-[10px] uppercase tracking-wider font-semibold'>
                                                        Views
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font/85 text-[11px] md:text-xs font-medium'
                                            style={{
                                                background:
                                                    "rgba(255,255,255,0.04)",
                                                border:
                                                    "1px solid rgba(255,255,255,0.1)",
                                            }}
                                        >
                                            <FiTrendingUp
                                                className='text-xs'
                                                style={{ color: "#FF8AFF" }}
                                            />
                                            Trending now
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>

                        {/* Soft fade-down divider */}
                        <div
                            aria-hidden
                            className='absolute bottom-0 inset-x-0 h-24 pointer-events-none'
                            style={{
                                background:
                                    "linear-gradient(to bottom, transparent, rgba(10,8,16,0.6))",
                            }}
                        />
                    </section>
                )
            )}

            {/* ===== GRID ===== */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-7xl'>
                    {/* DSPY1 — top-of-grid display ad, lazy-loaded */}
                    <ClientAdSense
                        slot='9729451986'
                        lazyLoad
                        containerClassName='w-full mb-6 md:mb-10'
                    />

                    {/* Section header */}
                    {!loading && listData.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex items-center justify-between mb-5 md:mb-6 mt-6 md:mt-10'
                        >
                            <div className='flex items-center gap-2'>
                                <div
                                    className='w-1 h-6 rounded-full'
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                    }}
                                />
                                <h2 className='text-client-font font-semibold text-sm md:text-base tracking-wide uppercase'>
                                    All Avatars
                                </h2>
                            </div>
                            <div className='ios-glass px-3 py-1 rounded-full text-client-font/75 text-[10px] md:text-xs'>
                                Showing{" "}
                                <span className='tabular-nums font-bold text-client-title'>
                                    {listData.length}
                                </span>
                                {totalList ? (
                                    <>
                                        <span className='text-client-font/55'> of </span>
                                        <span className='tabular-nums font-bold text-client-font'>
                                            {totalList}
                                        </span>
                                    </>
                                ) : null}
                            </div>
                        </motion.div>
                    )}

                    {/* Initial skeleton grid */}
                    {loading && page === 1 && (
                        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4'>
                            {[...Array(10)].map((_, i) => (
                                <CardSkeleton key={i} />
                            ))}
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && listData.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex flex-col items-center justify-center text-center py-16 md:py-24'
                        >
                            <div
                                className='w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-5'
                                style={{
                                    background:
                                        "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    boxShadow:
                                        "0 12px 28px -10px rgba(127,0,255,0.45)",
                                }}
                            >
                                <FiGrid className='text-2xl md:text-3xl text-client-title' />
                            </div>
                            <h3 className='text-client-font font-bold text-lg md:text-2xl tracking-tight'>
                                No avatars yet
                            </h3>
                            <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-md'>
                                This category is being curated. Check back soon for fresh
                                prompts.
                            </p>
                            <motion.button
                                whileHover={{ y: -2, scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                onClick={() => navigate("/")}
                                className='mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-xs md:text-sm'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                    boxShadow:
                                        "0 12px 28px -8px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                }}
                            >
                                Browse Categories
                            </motion.button>
                        </motion.div>
                    )}

                    {/* Grid */}
                    {listData.length > 0 && (
                        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4'>
                            {listData.map((item, idx) => (
                                <ItemCard
                                    key={item._id}
                                    item={item}
                                    index={idx}
                                    onClick={() => navigate(`/list/${item._id}`)}
                                />
                            ))}
                            {loadingMore &&
                                [...Array(5)].map((_, i) => (
                                    <CardSkeleton key={`s-${i}`} />
                                ))}
                        </div>
                    )}

                    {/* Infinite scroll trigger */}
                    {nextPage && (
                        <div
                            ref={observerTarget}
                            className='flex justify-center items-center py-8 md:py-12'
                        >
                            {loadingMore && (
                                <div className='ios-glass-dark inline-flex items-center gap-3 px-5 py-3 rounded-full'>
                                    <div className='w-4 h-4 rounded-full border-2 border-client-font/30 border-t-client-primary animate-spin' />
                                    <span className='text-client-font text-xs md:text-sm font-medium tracking-wide'>
                                        Loading more
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* End of list */}
                    {!nextPage && !loading && listData.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className='flex flex-col items-center gap-2 py-10 text-client-font/40'
                        >
                            <HiSparkles
                                className='text-2xl'
                                style={{ color: "#7F00FF" }}
                            />
                            <p className='text-xs md:text-sm tracking-wider uppercase'>
                                You've seen it all
                            </p>
                        </motion.div>
                    )}
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default ClientCategory;
